import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../services/api';

// TypeScript interfaces for API responses
export interface ExecutiveSummaryData {
  communication_style: string;
  strengths: string[];
  growth_opportunities: string[];
  overall_assessment: string;
}

export interface ExecutiveSummaryMetadata {
  generated_at: string;
  is_stale?: boolean;
  cached?: boolean;
  total_analyses?: number;
  date_range?: {
    earliest: string;
    latest: string;
    span_days: number;
  };
  change_report?: {
    reason: string;
    changes: string[];
    analyses_delta?: number;
  };
  ai_metadata?: {
    model_used: string;
    tokens: {
      input: number;
      output: number;
      total: number;
    };
    latency_ms: number;
    cost_usd?: number;
  };
}

export interface ExecutiveSummaryResponse {
  summary: ExecutiveSummaryData;
  metadata: ExecutiveSummaryMetadata;
}

export interface GenerateSummaryRequest {
  force?: boolean;
}

export interface InsufficientDataError {
  error: string;
  message: string;
  current_count: number;
  required_count: number;
}

/**
 * Hook to fetch the executive summary for the current user.
 * 
 * Features:
 * - Auto-fetches on mount
 * - Caches for 5 minutes
 * - Refetches on window focus
 * - Handles 404 (no summary exists)
 * 
 * @returns Query object with summary data, loading state, and error
 */
export const useExecutiveSummary = () => {
  return useQuery<ExecutiveSummaryResponse, Error>({
    queryKey: ['executiveSummary'],
    queryFn: async () => {
      const response = await apiClient.get<ExecutiveSummaryResponse>('/lite/api/executive-summary');
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true,
    retry: (failureCount, error: any) => {
      // Don't retry on 404 (no summary exists yet)
      if (error?.response?.status === 404) {
        return false;
      }
      // Retry up to 2 times for other errors
      return failureCount < 2;
    },
  });
};

/**
 * Hook to generate/regenerate an executive summary.
 * 
 * Features:
 * - POST to generate endpoint
 * - Invalidates cache on success
 * - Handles 202 (cached/no changes) vs 200 (newly generated)
 * - Handles 400 (insufficient data)
 * 
 * @returns Mutation object with mutate function, loading state, and error
 */
export const useGenerateSummary = () => {
  const queryClient = useQueryClient();

  return useMutation<ExecutiveSummaryResponse, Error, GenerateSummaryRequest>({
    mutationFn: async (request: GenerateSummaryRequest = {}) => {
      const response = await apiClient.post<ExecutiveSummaryResponse>(
        '/lite/api/executive-summary/generate',
        request,
        { 
          timeout: 90000 // 90 seconds for AI generation (can take 30-60s)
        }
      );
      return response.data;
    },
    onSuccess: (data) => {
      // Set the data directly (instant update)
      queryClient.setQueryData(['executiveSummary'], data);
      
      // Also invalidate to ensure fresh data on next mount
      queryClient.invalidateQueries({ queryKey: ['executiveSummary'] });
    },
  });
};

/**
 * Helper to check if an error is an insufficient data error.
 * 
 * @param error - Error object from mutation/query
 * @returns True if error is insufficient data (400 with specific message)
 */
export const isInsufficientDataError = (error: any): error is { response: { data: InsufficientDataError } } => {
  return error?.response?.status === 400 && error?.response?.data?.error === 'Insufficient data';
};

/**
 * Helper to check if response is cached (202 status or cached metadata flag).
 * 
 * @param response - Response from generate mutation
 * @returns True if summary was cached (no regeneration)
 */
export const isCachedResponse = (response: ExecutiveSummaryResponse): boolean => {
  return response?.metadata?.cached === true;
};

/**
 * Helper to extract insufficient data details from error.
 * 
 * @param error - Error object
 * @returns Insufficient data details or null
 */
export const getInsufficientDataDetails = (error: any): InsufficientDataError | null => {
  if (isInsufficientDataError(error)) {
    return error.response.data;
  }
  return null;
};


